import { Check, Clock, RefreshCw } from "lucide-react";
import { useDispatch } from "react-redux";

import type { ProviderDocumentRequestSchema } from "@/api/generated/types.gen";

import { Button } from "@components/ui/button";

import {
  REQUEST_STATUS_CHIP,
  REQUEST_STATUS_LABELS,
  REQUEST_TYPE_TABS,
} from "@constants/provider-requests";

import { useRefreshDocumentRequest } from "@hooks/mutations/use-provider-requests";

import { notifyError } from "@utils/notification";

type Props = {
  caseId: string;
  request: ProviderDocumentRequestSchema;
};

/**
 * The "Records request" status card shown once a request exists (only-once):
 * lifecycle chip (Requested → Receiving → Complete), a manual "Check for
 * updates" poll, per-item checklist, and progress. Completion is provider-
 * declared, not inferred from document count.
 */
export const RequestStatusCard = ({ caseId, request }: Props) => {
  const dispatch = useDispatch();
  const refresh = useRefreshDocumentRequest(caseId);

  const status = request.status as string;
  const isComplete = status === "COMPLETED";
  const items = request.items ?? [];
  const requestTypeLabel =
    REQUEST_TYPE_TABS.find((t) => t.id === request.request_type)?.label ??
    request.request_type;

  const handleCheck = async () => {
    try {
      await refresh.mutateAsync(request.id);
    } catch (err) {
      notifyError(err, dispatch, "Failed to check for updates");
    }
  };

  return (
    <div className="rounded-xl border border-border bg-background-white p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-foreground">Records request</h2>
          <p className="mt-1 text-sm text-foreground-muted">
            {request.reference_number ? `${request.reference_number} · ` : ""}
            SureMed · {requestTypeLabel}
          </p>
        </div>
        <span
          className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${
            REQUEST_STATUS_CHIP[status] ?? REQUEST_STATUS_CHIP.SUBMITTED
          }`}
        >
          {REQUEST_STATUS_LABELS[status] ?? status}
        </span>
      </div>

      <div className="mt-4 flex items-center justify-between gap-4">
        <p className="text-sm font-semibold text-foreground">
          {request.received_count ?? 0} / {request.total_count ?? items.length}{" "}
          received
        </p>
        {!isComplete && (
          <Button
            variant="outline"
            onClick={handleCheck}
            disabled={refresh.isPending}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${refresh.isPending ? "animate-spin" : ""}`}
            />
            {refresh.isPending ? "Checking…" : "Check for updates"}
          </Button>
        )}
      </div>

      {/* Per-item checklist */}
      <ul className="mt-4 divide-y divide-border rounded-lg border border-border">
        {items.map((item) => (
          <li
            key={item.code}
            className="flex items-center gap-3 px-4 py-2.5 text-sm"
          >
            {item.received ? (
              <Check className="h-4 w-4 shrink-0 text-green-600" />
            ) : (
              <Clock className="h-4 w-4 shrink-0 text-foreground-muted/50" />
            )}
            <span className="text-foreground">{item.label}</span>
            <span
              className={`ml-auto text-xs font-medium ${
                item.received ? "text-green-700" : "text-foreground-muted"
              }`}
            >
              {item.received ? "Received" : "Awaiting"}
            </span>
          </li>
        ))}
      </ul>

      {isComplete ? (
        <p className="mt-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">
          Request complete — SureMed has finished sending. All records are attached
          to this case and split into Assessment prep.
        </p>
      ) : (
        <p className="mt-3 text-xs text-foreground-muted">
          Status is confirmed by the provider, not by documents arriving. Collected
          documents are added to this case as they come in.
        </p>
      )}
    </div>
  );
};
